import { SearchHelpers } from '@/client/legacy-shared-modules/search/helpers';
import {
  EEventSearchType,
  IEventSearch,
  IFileSearch,
  IMemberSearch,
} from '@/client/legacy-shared-modules/search/search-types';

export type AiAskIndexData = {
  postIndexName: string;
  orgIndexName: string;
  eventIndexName: string;
  fileIndexName: string;
  memberIndexName: string;
  memberIndexSearchableField: string;
  memberIndexViewableField: string;
};

export function indexLabelForName(
  indexData: AiAskIndexData | undefined,
  indexName: string
): string {
  const n = (indexName || '').trim();
  if (!indexData || !n) return indexName || 'Result';
  if (n === indexData.postIndexName) return 'Posts';
  if (n === indexData.memberIndexName) return 'Members';
  if (n === indexData.orgIndexName) return 'Organizations';
  if (n === indexData.eventIndexName) return 'Events';
  if (n === indexData.fileIndexName) return 'Files';
  return n;
}

const SOURCE_INDEX_LABELS: Record<string, string> = {
  posts: 'Posts',
  members: 'Members',
  organizations: 'Organizations',
  events: 'Events',
  files: 'Files',
};

function normalizeSourceIndexType(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/^(ai|div|tw)_/, '');
}

export function sourceIndexTypeDisplayLabel(source: unknown): string {
  if (!source || typeof source !== 'object') return '';
  const s = source as Record<string, unknown>;
  const it = normalizeSourceIndexType(s.index_type ?? s.indexType);
  if (!it) return '';
  return SOURCE_INDEX_LABELS[it] || it;
}

export function sourceObjectId(source: unknown): string {
  if (!source || typeof source !== 'object') return '';
  const s = source as Record<string, unknown>;
  return String(s.object_id ?? s.doc_id ?? s.objectID ?? '').trim();
}

export function isAiAskFileSource(source: unknown): boolean {
  if (!source || typeof source !== 'object') return false;
  const s = source as Record<string, unknown>;
  return normalizeSourceIndexType(s.index_type ?? s.indexType) === 'files';
}

/** File sources without a group route open via `?versionFileId=` + VersionFilePreviewFromQuery. */
export function isVersionFilePreviewSource(source: unknown): boolean {
  if (!isAiAskFileSource(source)) return false;
  const s = source as Record<string, unknown>;
  const groupId =
    typeof s.groupId === 'string'
      ? s.groupId.trim()
      : typeof s.group_id === 'string'
        ? s.group_id.trim()
        : '';
  return !groupId && Boolean(sourceObjectId(source));
}

export function sourceHref(source: unknown, appOrigin: string): string {
  if (!source || typeof source !== 'object') return '';
  const s = source as Record<string, unknown>;
  const indexType = normalizeSourceIndexType(s.index_type ?? s.indexType);
  const sourceId = sourceObjectId(source);
  const origin = appOrigin.replace(/\/$/, '');

  const withOrigin = (route: string): string => {
    if (!route) return '';
    if (/^https?:\/\//i.test(route)) return route;
    const path = route.startsWith('/') ? route : `/${route}`;
    return origin ? `${origin}${path}` : path;
  };

  // Frontend owns app routes (SearchHelpers). Ignore Ask `url` for known entities —
  // indexed paths like /community/event/... are not real client routes.
  if (sourceId) {
    if (indexType === 'events') {
      return withOrigin(
        SearchHelpers.getEventUrl({
          objectID: sourceId,
          eventType: toEventSearchType(s.eventType ?? s.type),
        } as unknown as IEventSearch)
      );
    }
    if (indexType === 'members') {
      return withOrigin(
        SearchHelpers.getMemberUrl({
          objectID: sourceId,
          userId: String(s.userId ?? sourceId),
        } as unknown as IMemberSearch)
      );
    }
    if (indexType === 'posts') {
      // Ask sources only carry id — use InstantSearch fallback route.
      return withOrigin(`/community/post/${encodeURIComponent(sourceId)}`);
    }
    if (indexType === 'files') {
      const groupId =
        typeof s.groupId === 'string'
          ? s.groupId.trim()
          : typeof s.group_id === 'string'
            ? s.group_id.trim()
            : '';
      if (groupId) {
        return withOrigin(
          SearchHelpers.getFileUrl({
            objectID: sourceId,
            groupId,
            scope: s.scope,
            originalPostId:
              typeof s.originalPostId === 'string'
                ? s.originalPostId
                : undefined,
          } as unknown as IFileSearch)
        );
      }
      // Stay on the current page; VersionFilePreviewFromQuery opens the modal.
      return `?versionFileId=${encodeURIComponent(sourceId)}`;
    }
    if (indexType === 'organizations') {
      // Match InstantSearch Organization hit links.
      return withOrigin(`/companies/${encodeURIComponent(sourceId)}`);
    }
  }

  // Unknown entity: fall back to whatever link/path the payload carried.
  const link = s.link ?? s.url;
  if (typeof link === 'string' && link.trim()) {
    return withOrigin(link.trim());
  }
  const path = s.path;
  if (typeof path === 'string' && path.trim()) {
    return withOrigin(path.trim());
  }
  return '';
}

function toEventSearchType(raw: unknown): EEventSearchType {
  const text = String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[-_\s]/g, '');
  if (
    text === 'CONFERENCE' ||
    text === 'CONFERENCEEVENT' ||
    text === 'CONFERENCEEVENTV2'
  ) {
    return EEventSearchType.CONFERENCE;
  }
  if (text === 'SIMPLE' || text === 'SIMPLEEVENT' || text === 'SIMPLEEVENTV2') {
    return EEventSearchType.SIMPLE;
  }
  // SearchHelpers falls back to /events/:id for unknown types.
  return EEventSearchType.SIMPLE;
}

export function sourceIndexLabel(source: unknown): string {
  if (!source || typeof source !== 'object') return '';
  const s = source as Record<string, unknown>;
  const it = s.index_type ?? s.indexType;
  return typeof it === 'string' ? it : '';
}

export function sourceDisplayTitle(
  source: unknown,
  ordinal: number,
  indexData?: AiAskIndexData
): string {
  if (!source || typeof source !== 'object') return `Source ${ordinal}`;
  const s = source as Record<string, unknown>;
  const raw = s.title ?? s.label;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  const it = sourceIndexLabel(source);
  const idxLabel = indexData ? indexLabelForName(indexData, it) : it;
  const oid = String(s.object_id ?? s.doc_id ?? s.objectID ?? '').trim();
  if (idxLabel && oid) {
    return `${idxLabel}: ${oid.length > 14 ? `${oid.slice(0, 14)}…` : oid}`;
  }
  if (oid) return oid;
  return `Source ${ordinal}`;
}

export function refToPseudoSource(r: unknown): Record<string, unknown> | null {
  if (!r || typeof r !== 'object') return null;
  const o = r as Record<string, unknown>;
  return {
    title: o.title,
    label: o.label,
    index_type: o.type,
    object_id: o.id,
    link: o.link ?? o.url,
    path: o.path,
    url: o.url,
  };
}

export function isNoContextAnswer(answer: string | null | undefined): boolean {
  const text = String(answer ?? '')
    .trim()
    .toLowerCase();
  if (!text) return false;
  return (
    text === 'not found in provided context.' ||
    text === 'not found in provided context' ||
    text.includes('cannot answer from the provided materials') ||
    text.includes('insufficient context')
  );
}

export function citedOrdinalsFromAnswer(
  answer: string | null | undefined
): number[] {
  const text = String(answer ?? '');
  const out: number[] = [];
  const seen = new Set<number>();
  const re = /\[S(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const n = parseInt(m[1], 10);
    if (!Number.isFinite(n) || n < 1 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

export function filterSourcesByCitations(
  answer: string | null | undefined,
  sources: unknown[] | undefined,
  references: unknown[] | undefined
): { sources: unknown[]; references: unknown[] } {
  const ords = citedOrdinalsFromAnswer(answer);
  const maxVisible = 4;
  if (!ords.length) {
    return {
      sources: Array.isArray(sources) ? sources.slice(0, maxVisible) : [],
      references: Array.isArray(references)
        ? references.slice(0, maxVisible)
        : [],
    };
  }
  const filteredSources: unknown[] = [];
  const filteredRefs: unknown[] = [];
  const usedSourceIdx = new Set<number>();
  const usedRefIdx = new Set<number>();
  for (const n of ords) {
    const i = n - 1;
    if (Array.isArray(sources) && sources[i]) {
      filteredSources.push(sources[i]);
      usedSourceIdx.add(i);
    } else if (Array.isArray(references) && references[i]) {
      filteredRefs.push(references[i]);
      usedRefIdx.add(i);
    }
    if (filteredSources.length + filteredRefs.length >= maxVisible) break;
  }
  if (Array.isArray(sources)) {
    for (
      let i = 0;
      i < sources.length &&
      filteredSources.length + filteredRefs.length < maxVisible;
      i += 1
    ) {
      if (usedSourceIdx.has(i)) continue;
      filteredSources.push(sources[i]);
      usedSourceIdx.add(i);
    }
  } else if (Array.isArray(references)) {
    for (
      let i = 0;
      i < references.length &&
      filteredSources.length + filteredRefs.length < maxVisible;
      i += 1
    ) {
      if (usedRefIdx.has(i)) continue;
      filteredRefs.push(references[i]);
      usedRefIdx.add(i);
    }
  }
  return { sources: filteredSources, references: filteredRefs };
}

export function stripHtml(s: string): string {
  if (typeof s !== 'string') return '';
  return s.replace(/<[^>]*>/g, '');
}

export function getSnippetFromHit(hit: Record<string, unknown>): string {
  const sn =
    (hit._snippetResult as Record<string, { value?: string }> | undefined) ||
    {};
  const hl =
    (hit._highlightResult as Record<string, { value?: string }> | undefined) ||
    {};
  const field =
    sn.bodyPlainText ||
    sn.displayName ||
    sn.title ||
    sn.name ||
    sn['file.name'] ||
    (sn.file as { name?: { value?: string } } | undefined)?.name;
  if (field && typeof field === 'object' && 'value' in field && field.value) {
    return field.value;
  }
  const h =
    hl.title ||
    hl.displayName ||
    hl.bodyPlainText ||
    hl.name ||
    hl['file.name'] ||
    (hl.file as { name?: { value?: string } } | undefined)?.name;
  if (h && typeof h === 'object' && 'value' in h && h.value) return h.value;
  return (
    (hit.bodyPlainText as string) ||
    (hit.title as string) ||
    (hit.displayName as string) ||
    (hit.name as string) ||
    (hit['file.name'] as string) ||
    ((hit.file as { name?: string } | undefined)?.name ?? '') ||
    ''
  );
}

export function getTitleFromHit(hit: Record<string, unknown>): string {
  const hl =
    (hit._highlightResult as Record<string, { value?: string }> | undefined) ||
    {};
  return (
    (hit.title as string) ||
    hl.title?.value ||
    (hit.displayName as string) ||
    hl.displayName?.value ||
    (hit.name as string) ||
    hl.name?.value ||
    (hit['file.name'] as string) ||
    hl['file.name']?.value ||
    ((hit.file as { name?: { value?: string } } | undefined)?.name?.value ??
      (hit.file as { name?: string } | undefined)?.name) ||
    (hit.fileName as string) ||
    (hit.objectID as string) ||
    ''
  );
}
